import { useEffect, useRef, useState } from "react";
import { useDialogFocus } from "@hybrid-iptv/ui";
import {
  REMOTE_ACTIONS,
  REMOTE_BUTTONS,
  actionLabel,
  type RemoteActionId,
  type RemoteButton,
  type RemoteButtonId,
} from "../remote/remoteMap.js";
import { useRemoteMap } from "../remote/remoteMapStore.js";
import { ProLockedPanel } from "../components/ProLockedPanel.js";
import { useFeatureUnlocked } from "../library/proFeatures.js";

/**
 * Settings → Remote control: what every button does, and a different action
 * for a press or a long press on any of them (remote/remoteMap.ts). Sparkle's
 * page, with one rule of ours: the arrows and OK change only for watching full
 * screen, so the menus always move the way they look.
 */

type Kind = "press" | "longPress";

/** The words for a button's current action, and whether it is the viewer's own. */
function describe(button: RemoteButton, kind: Kind, chosen: RemoteActionId | undefined): { text: string; custom: boolean } {
  if (chosen) return { text: actionLabel(chosen), custom: true };
  const fallback = kind === "press" ? button.press : button.longPress;
  if (kind === "press" && button.pressText) return { text: button.pressText, custom: false };
  return { text: fallback ? actionLabel(fallback.action) : actionLabel("none"), custom: false };
}

function ActionPicker({
  button,
  kind,
  current,
  onPick,
  onReset,
  onClose,
}: {
  button: RemoteButton;
  kind: Kind;
  current: RemoteActionId | undefined;
  onPick: (action: RemoteActionId) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useDialogFocus({ open: true, onClose, rootRef });
  // Open on the action it has now, not the top of a long list.
  useEffect(() => {
    rootRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]')?.focus();
  }, []);

  const fallback = kind === "press" ? button.press : button.longPress;
  const groups: Array<{ title: string; group: "navigation" | "player" }> = [
    { title: "Go to", group: "navigation" },
    { title: "Player", group: "player" },
  ];

  return (
    <div className="remote-picker__backdrop">
      <div className="remote-picker" role="dialog" aria-modal="true" aria-label={`${button.label}: ${kind === "press" ? "press" : "long press"}`} ref={rootRef}>
        <h2 className="remote-picker__title">
          {button.label} — {kind === "press" ? "press" : "long press"}
        </h2>
        <div className="remote-picker__list">
          <button type="button" className="remote-picker__option" aria-pressed={current === undefined} onClick={onReset}>
            Default: {kind === "press" && button.pressText ? button.pressText : fallback ? actionLabel(fallback.action) : actionLabel("none")}
          </button>
          <button type="button" className="remote-picker__option" aria-pressed={current === "none"} onClick={() => onPick("none")}>
            {actionLabel("none")} (the button does nothing)
          </button>
          {groups.map(({ title, group }) => (
            <div key={group} className="remote-picker__group">
              <h3 className="remote-picker__group-title">{title}</h3>
              {REMOTE_ACTIONS.filter((action) => action.group === group).map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className="remote-picker__option"
                  aria-pressed={current === action.id}
                  onClick={() => onPick(action.id)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ButtonRow({ button, onOpen }: { button: RemoteButton; onOpen: (button: RemoteButtonId, kind: Kind) => void }) {
  const chosen = useRemoteMap((state) => state.overrides[button.id]);
  const press = describe(button, "press", chosen?.press);
  const long = describe(button, "longPress", chosen?.longPress);
  return (
    <div className="remote-row">
      <div className="remote-row__name">
        <span className="remote-row__label">{button.label}</span>
        <span className="remote-row__menus">In menus: {button.inMenus}</span>
      </div>
      <button type="button" className={`remote-row__action${press.custom ? " remote-row__action--custom" : ""}`} onClick={() => onOpen(button.id, "press")}>
        <span className="remote-row__kind">Press</span>
        <span className="remote-row__value">{press.text}</span>
      </button>
      <button type="button" className={`remote-row__action${long.custom ? " remote-row__action--custom" : ""}`} onClick={() => onOpen(button.id, "longPress")}>
        <span className="remote-row__kind">Long press</span>
        <span className="remote-row__value">{long.text}</span>
      </button>
    </div>
  );
}

export function RemoteSettingsPage() {
  // Custom remote control is Pro (ADR 2026-10-10); the standard buttons keep working.
  const unlocked = useFeatureUnlocked("customRemote");
  return unlocked ? <RemoteSettingsContent /> : <ProLockedPanel feature="customRemote" />;
}

function RemoteSettingsContent() {
  const overrides = useRemoteMap((state) => state.overrides);
  const choose = useRemoteMap((state) => state.choose);
  const clear = useRemoteMap((state) => state.clear);
  const resetAll = useRemoteMap((state) => state.resetAll);
  const [picking, setPicking] = useState<{ button: RemoteButtonId; kind: Kind } | null>(null);
  /** The row button that opened the picker, to give focus back to. */
  const opener = useRef<HTMLElement | null>(null);

  const open = (button: RemoteButtonId, kind: Kind) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPicking({ button, kind });
  };
  const close = () => {
    setPicking(null);
    // Back where the viewer was, not the top of the page.
    setTimeout(() => opener.current?.focus(), 0);
  };

  const pickingButton = picking ? REMOTE_BUTTONS.find((button) => button.id === picking.button) : undefined;
  const changed = Object.keys(overrides).length > 0;
  const watching = REMOTE_BUTTONS.filter((button) => button.watchingOnly);
  const others = REMOTE_BUTTONS.filter((button) => !button.watchingOnly);

  return (
    <div className="page page--remote-settings">
      <div className="settings-card">
        <div className="settings-header">
          <h1 className="settings-title">Remote control</h1>
          <p className="settings-subtitle">
            What each button on your remote does. Choose a different action for a press or a long press. A
            long press is holding the button for over half a second. Buttons your remote does not have are
            simply never pressed.
          </p>
        </div>

        <h2 className="remote-section">While watching full screen</h2>
        <p className="remote-section__hint">
          The arrows and OK change only here, so the menus always move the way they look.
        </p>
        {watching.map((button) => (
          <ButtonRow key={button.id} button={button} onOpen={open} />
        ))}
        <div className="remote-row remote-row--fixed">
          <div className="remote-row__name">
            <span className="remote-row__label">Back</span>
            <span className="remote-row__menus">Always steps back: closes what is open, then the previous screen</span>
          </div>
        </div>

        <h2 className="remote-section">Other buttons — everywhere</h2>
        {others.map((button) => (
          <ButtonRow key={button.id} button={button} onOpen={open} />
        ))}

        <div className="remote-reset">
          <button type="button" className="ui-btn" disabled={!changed} onClick={resetAll}>
            Reset every button to its default
          </button>
        </div>
      </div>

      {picking && pickingButton && (
        <ActionPicker
          button={pickingButton}
          kind={picking.kind}
          current={overrides[picking.button]?.[picking.kind]}
          onPick={(action) => {
            choose(picking.button, picking.kind, action);
            close();
          }}
          onReset={() => {
            clear(picking.button, picking.kind);
            close();
          }}
          onClose={close}
        />
      )}
    </div>
  );
}
